/**
 * ============================================================================
 * KbGovernanceSidebar — Painel de governança da página KB.
 *
 * Seções (padrão de cartão editorial):
 *   - Classificação de dados
 *   - Ciclo de revisão + freshness
 *   - Taxonomias (multi-seletor por dimensão)
 *   - Controle de acesso (KbAclManager)
 *   - Versões (KbVersionsTimeline)
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.2.0 - Refatoração visual: layout em cartões, tipografia
 *            hierárquica, grid responsivo. Absorveu bloco de "Versões" do parent.
 *   RELEASE: 2026-07-08 v1.1.0 - Fase C: nova seção "Acesso" (ACL por página).
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix.
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Info, Tags as TagsIcon, ShieldCheck, GitBranch, ClipboardList, X, Link2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useKbTaxonomies, useKbAssignTaxonomy, useKbPageTaxonomies, KbTaxonomyDimension } from "@/hooks/kb/useKbEditorial";
import { KbAclManager } from "@/components/kb/KbAclManager";
import { KbVersionsTimeline } from "@/components/kb/editor/KbVersionsTimeline";
import { KbBacklinksPanel } from "@/components/kb/editor/KbBacklinksPanel";

interface Props {
  pageId: string;
  classification: string;
  reviewCycleDays: number;
  lastReviewedAt?: string | null;
  onClassificationChange: (v: string) => void;
  onReviewCycleChange: (v: number) => void;
  canManageClassification: boolean;
}

const CLASSIFICATIONS: Array<{ value: string; label: string; help: string }> = [
  { value: "public", label: "Público", help: "Pode aparecer em wiki pública e ser indexado por IA para qualquer contexto." },
  { value: "internal", label: "Interno", help: "Membros da empresa podem ler. Indexação de IA restrita à empresa." },
  { value: "confidential", label: "Confidencial", help: "Só quem estiver na lista de acesso da página. IA restrita à empresa." },
  { value: "restricted", label: "Restrito", help: "Regra de acesso explícita obrigatória. IA restrita à empresa." },
  { value: "secret", label: "Secreto", help: "Nunca indexado por IA. Somente acesso via regra explícita." },
];

const DIMENSIONS: Array<{ value: KbTaxonomyDimension; label: string }> = [
  { value: "department", label: "Departamento" },
  { value: "process", label: "Processo" },
  { value: "product", label: "Produto" },
  { value: "module", label: "Módulo" },
  { value: "rule", label: "Regra" },
  { value: "policy", label: "Política" },
  { value: "training", label: "Treinamento" },
  { value: "faq", label: "FAQ" },
];

// ---------------------------------------------------------------------------
// Sub-componentes visuais reutilizáveis dentro do sidebar
// ---------------------------------------------------------------------------

/** Cartão de seção com header (ícone + título + descrição opcional + slot direito). */
function SectionCard({
  icon: Icon,
  title,
  description,
  right,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card overflow-hidden">
      <header className="flex items-start justify-between gap-3 px-4 py-3 border-b bg-muted/30">
        <div className="flex items-start gap-2 min-w-0">
          <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-foreground leading-tight">{title}</h3>
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
            )}
          </div>
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Label padronizado das seções (uppercase discreto). */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Label className="mb-1.5 block text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </Label>
  );
}

function FreshnessBadge({ lastReviewedAt, cycleDays }: { lastReviewedAt?: string | null; cycleDays: number }) {
  if (!lastReviewedAt) return <Badge variant="outline" className="text-[10px]">nunca revisado</Badge>;
  const ageDays = (Date.now() - new Date(lastReviewedAt).getTime()) / 86400000;
  const ratio = ageDays / Math.max(1, cycleDays);
  if (ratio > 1) return <Badge variant="destructive" className="text-[10px]">vencido ({Math.floor(ageDays)}d)</Badge>;
  if (ratio > 0.8) return <Badge className="text-[10px]">quase vencendo</Badge>;
  return <Badge variant="secondary" className="text-[10px]">em dia</Badge>;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function KbGovernanceSidebar(props: Props) {
  const { pageId, classification, reviewCycleDays, lastReviewedAt, onClassificationChange, onReviewCycleChange, canManageClassification } = props;

  const { data: allTaxonomies } = useKbTaxonomies();
  const { data: pageTaxonomies } = useKbPageTaxonomies(pageId);
  const assign = useKbAssignTaxonomy();

  const currentIds = useMemo(() => new Set((pageTaxonomies ?? []).map((t: any) => t.id)), [pageTaxonomies]);
  const [openDim, setOpenDim] = useState<KbTaxonomyDimension | null>(null);

  const toggle = (taxonomyId: string) => {
    const next = new Set(currentIds);
    if (next.has(taxonomyId)) next.delete(taxonomyId); else next.add(taxonomyId);
    assign.mutate({ page_id: pageId, taxonomy_ids: Array.from(next) as string[] });
  };

  const activeClassification = CLASSIFICATIONS.find((c) => c.value === classification);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        {/* Classificação de dados */}
        <SectionCard
          icon={ClipboardList}
          title="Classificação de dados"
          description="Define quem lê e como a IA indexa o conteúdo."
          right={
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                <b>Secret</b> desativa qualquer indexação de IA. <b>Público</b> permite aparecer em wiki pública.
              </TooltipContent>
            </Tooltip>
          }
        >
          <FieldLabel>Nível</FieldLabel>
          <Select value={classification} onValueChange={onClassificationChange} disabled={!canManageClassification}>
            <SelectTrigger className="w-full h-auto min-h-9 py-2"><SelectValue /></SelectTrigger>
            <SelectContent className="max-w-[--radix-select-trigger-width]">
              {CLASSIFICATIONS.map((c) => (
                <SelectItem key={c.value} value={c.value} className="py-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{c.label}</span>
                    <span className="text-xs text-muted-foreground leading-snug whitespace-normal">
                      {c.help}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SectionCard>

        {/* Ciclo de revisão + freshness */}
        <SectionCard
          icon={Info}
          title="Ciclo de revisão"
          description="Frequência esperada de revisão do conteúdo."
        >
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <FieldLabel>Intervalo (dias)</FieldLabel>
              <Input
                type="number"
                min={1}
                max={3650}
                value={reviewCycleDays}
                onChange={(e) => onReviewCycleChange(Number(e.target.value) || 1)}
                className="w-28 h-9"
              />
            </div>
            <div className="pb-1.5">
              <FreshnessBadge lastReviewedAt={lastReviewedAt} cycleDays={reviewCycleDays} />
            </div>
          </div>
        </SectionCard>

        {/* Taxonomias */}
        <SectionCard
          icon={TagsIcon}
          title="Taxonomias"
          description="Organize a página em dimensões para busca e IA."
        >
          {/* Chips atribuídas */}
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[24px]">
            {(pageTaxonomies ?? []).map((t: any) => (
              <Badge
                key={t.id}
                variant="secondary"
                className="text-[11px] pl-2 pr-1 gap-1 cursor-pointer hover:bg-secondary/70 transition-colors"
                onClick={() => toggle(t.id)}
              >
                <span>{t.name}</span>
                <X className="h-3 w-3 opacity-60" />
              </Badge>
            ))}
            {(pageTaxonomies ?? []).length === 0 && (
              <span className="text-xs text-muted-foreground">Nenhuma taxonomia atribuída.</span>
            )}
          </div>

          {/* Botões por dimensão em grid — evita quebra desalinhada */}
          <Popover open={openDim !== null} onOpenChange={(o) => !o && setOpenDim(null)}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {DIMENSIONS.map((d) => (
                <PopoverTrigger asChild key={d.value}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs font-normal justify-start"
                    onClick={() => setOpenDim(d.value)}
                  >
                    <span className="text-muted-foreground mr-1">+</span>
                    {d.label}
                  </Button>
                </PopoverTrigger>
              ))}
            </div>
            <PopoverContent className="w-64 max-h-64 overflow-auto">
              <div className="text-xs font-medium mb-2">
                {DIMENSIONS.find((d) => d.value === openDim)?.label}
              </div>
              <div className="space-y-1">
                {(allTaxonomies ?? []).filter((t: any) => t.dimension === openDim).map((t: any) => (
                  <button
                    key={t.id}
                    onClick={() => toggle(t.id)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-accent"
                  >
                    <span>{t.name}</span>
                    {currentIds.has(t.id) && <span className="text-primary">✓</span>}
                  </button>
                ))}
                {(allTaxonomies ?? []).filter((t: any) => t.dimension === openDim).length === 0 && (
                  <div className="text-xs text-muted-foreground">Nenhuma taxonomia nesta dimensão.</div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </SectionCard>

        {/* Controle de acesso (Fase C/D) */}
        <SectionCard
          icon={ShieldCheck}
          title="Controle de acesso"
          description="Concessões herdadas do space/coleção e específicas desta página."
          right={
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                Concessões no <b>space</b> ou <b>coleção</b> são herdadas por todas as páginas
                descendentes. Herdadas só podem ser removidas no nível pai.
              </TooltipContent>
            </Tooltip>
          }
        >
          <KbAclManager scope="page" scopeId={pageId} canManage={canManageClassification} embedded />
        </SectionCard>

        {/* Referenciado em (backlinks wiki) */}
        <SectionCard
          icon={Link2}
          title="Referenciado em"
          description="Artigos que apontam para esta página (wiki-links)."
        >
          <KbBacklinksPanel pageId={pageId} pageSize={10} compact />
        </SectionCard>

        {/* Versões */}
        <SectionCard
          icon={GitBranch}
          title="Versões"
          description="Histórico de publicações e ajustes desta página."
        >
          <KbVersionsTimeline pageId={pageId} />
        </SectionCard>
      </div>
    </TooltipProvider>
  );
}
